import { useEffect, useMemo, useState } from 'react';
import { adminApi, Client, ClientAccountListItem } from '../api/admin';
import {
  companyReportsApi,
  CompanyActivityReportQuery,
  CompanyActivityReportResponse,
  CompanyReportSection,
} from '../api/companyReports';
import { useSelectedClientStore } from '../store/selectedClientStore';
import EnglishDatePicker from '../components/common/EnglishDatePicker';

const sections: Array<{ value: CompanyReportSection; label: string }> = [
  { value: 'all', label: 'All activity' },
  { value: 'incoming', label: 'Incoming' },
  { value: 'outgoing', label: 'Outgoing' },
  { value: 'fx', label: 'FX' },
  { value: 'ledger', label: 'Ledger' },
];

function monthStart(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
}

function nextDay(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)).toISOString().slice(0, 10);
}

function toUtc(value: string, end = false): string {
  if (!end) return `${value}T00:00:00Z`;
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString();
}

function shortId(value?: string): string {
  return value ? `${value.slice(0, 8)}…` : '—';
}

function money(value?: number): string {
  return typeof value === 'number' ? value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '—';
}

function clientLabel(client: Client): string {
  return client.companyName || `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim() || client.email;
}

export default function CompanyActivityReport() {
  const selectedClient = useSelectedClientStore((state) => state.selectedClient);
  const clearSelectedClient = useSelectedClientStore((state) => state.clearSelectedClient);
  const [client, setClient] = useState<Client | null>(selectedClient as Client | null);
  const [accounts, setAccounts] = useState<ClientAccountListItem[]>([]);
  const [accountId, setAccountId] = useState('');
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(nextDay);
  const [section, setSection] = useState<CompanyReportSection>('all');
  const [report, setReport] = useState<CompanyActivityReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Client[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);

  const query = useMemo<CompanyActivityReportQuery | null>(() => {
    if (!client?.id || !from || !to) return null;
    return {
      clientId: client.id,
      ...(accountId ? { accountId } : {}),
      from: toUtc(from),
      to: toUtc(to, true),
      section,
      page: 1,
      pageSize: 100,
    };
  }, [accountId, client?.id, from, section, to]);

  useEffect(() => {
    if (!client?.id) {
      setAccounts([]);
      setAccountId('');
      return;
    }
    adminApi.getClientAccountsByClientId(client.id)
      .then((response) => setAccounts(response.data ?? []))
      .catch(() => setAccounts([]));
  }, [client?.id]);

  useEffect(() => {
    if (search.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = window.setTimeout(() => {
      setSearchLoading(true);
      adminApi.searchClients(search.trim())
        .then((response) => {
          const raw = Array.isArray(response.data)
            ? response.data
            : (response.data as { data?: Client[] })?.data ?? [];
          setSearchResults((raw as Client[]).filter((item) => item?.id));
        })
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const selectClient = (item: Client) => {
    setClient(item);
    setAccountId('');
    setSearch(clientLabel(item));
    setSearchResults([]);
    setSearchFocused(false);
    setReport(null);
  };

  const load = async () => {
    if (!query) {
      setError('Select a company and date range.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setReport(await companyReportsApi.get(query));
    } catch (e) {
      setReport(null);
      setError(e instanceof Error ? e.message : 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    if (!query) return;
    setDownloading(true);
    setError(null);
    try {
      await companyReportsApi.downloadCsv(query);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to download report.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Company activity report</h1>
          <p className="text-gray-600">Payments, FX operations, and ledger movements for reconciliation.</p>
        </div>
        <button
          type="button"
          onClick={download}
          disabled={!query || downloading}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {downloading ? 'Downloading…' : 'Download CSV'}
        </button>
      </div>

      <div className="mb-5 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <label className="relative text-sm text-gray-700">
            Company
            <input
              value={search}
              onFocus={() => setSearchFocused(true)}
              onChange={(e) => {
                setSearch(e.target.value);
                setClient(null);
                setAccountId('');
                setReport(null);
                setSearchFocused(true);
              }}
              placeholder="Search company, email, or public ID"
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2"
            />
            {searchFocused && search.trim().length >= 2 && (
              <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded border border-gray-200 bg-white shadow-lg">
                {searchLoading && <p className="px-3 py-2 text-sm text-gray-500">Searching companies…</p>}
                {searchResults.map((item) => (
                  <button key={item.id} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => selectClient(item)}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50">
                    <span className="block font-medium text-gray-900">{clientLabel(item)}</span>
                    <span className="block text-xs text-gray-500">{item.publicId || item.email}</span>
                  </button>
                ))}
                {!searchLoading && searchResults.length === 0 && <p className="px-3 py-2 text-sm text-gray-500">No companies found.</p>}
              </div>
            )}
          </label>
          <label className="text-sm text-gray-700">
            Account
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)} disabled={!client}
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 disabled:bg-gray-100">
              <option value="">All accounts</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.accountName || shortId(account.id)} · {account.currencyId} · {account.providerName || 'Provider not specified'}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-700">From
            <EnglishDatePicker value={from} onChange={setFrom} className="mt-1 rounded border border-gray-300 px-3 py-2" />
          </label>
          <label className="text-sm text-gray-700">To
            <EnglishDatePicker value={to} onChange={setTo} className="mt-1 rounded border border-gray-300 px-3 py-2" />
          </label>
          <div className="flex items-end gap-2">
            <button type="button" onClick={load} disabled={!query || loading} className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              {loading ? 'Loading…' : 'Apply'}
            </button>
            {selectedClient && <button type="button" onClick={() => { clearSelectedClient(); setClient(null); setAccountId(''); }} className="pb-2 text-sm text-indigo-600">Clear</button>}
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {sections.map((item) => (
          <button key={item.value} type="button" onClick={() => setSection(item.value)}
            className={`rounded-lg px-3 py-1.5 text-sm ${section === item.value ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
            {item.label}
          </button>
        ))}
      </div>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
      {client && <p className="mb-3 text-sm text-gray-600">Selected company: <span className="font-medium text-gray-900">{clientLabel(client)}</span></p>}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600">
            <tr>
              <th className="px-3 py-2">Date UTC</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Reference</th>
              <th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2">Currency</th><th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Details</th><th className="px-3 py-2">FX</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {report?.items.map((row) => (
              <tr key={`${row.rowType}-${row.id}-${row.accountRole}`} className="hover:bg-gray-50">
                <td className="whitespace-nowrap px-3 py-2">{new Date(row.occurredAt).toISOString().replace('T', ' ').slice(0, 19)}</td>
                <td className="px-3 py-2">{row.rowType}{row.accountRole ? ` · ${row.accountRole}` : ''}</td>
                <td className="max-w-44 truncate px-3 py-2 font-mono text-xs" title={row.reference}>{row.reference}</td>
                <td className="px-3 py-2 text-right font-mono">{money(row.amount)}</td><td className="px-3 py-2">{row.currency}</td><td className="px-3 py-2">{row.status || '—'}</td>
                <td className="px-3 py-2 text-xs">{row.counterparty || row.operation || (row.businessObjectType ? `${row.businessObjectType} ${shortId(row.businessObjectId)}` : '—')}</td>
                <td className="px-3 py-2 text-xs">{row.clientRate ? `Rate ${row.clientRate}` : row.profitAmount ? `Profit ${money(row.profitAmount)} ${row.profitCurrency ?? ''}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && report?.items.length === 0 && <p className="p-4 text-sm text-gray-500">No activity for the selected filters.</p>}
        {!loading && !report && <p className="p-4 text-sm text-gray-500">Select a company and apply filters to view activity.</p>}
      </div>
      {report && <p className="mt-3 text-sm text-gray-500">Showing {report.items.length} of {report.totalCount} entries.</p>}
    </div>
  );
}
